import { CheckIcon } from "@heroicons/react/24/outline";
import { useEffect, useState, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { confirmPasswordReset } from "../../api/accountsApi";
import { appPaths } from "../../shared/routing/appPaths";
import ErrorNotice from "../../errors/ErrorNotice";
import { useSubmission } from "../../shared/state/useSubmission";
import AppButton from "../../ui/buttons/AppButton";
import StatusMessage from "../../ui/feedback/StatusMessage";
import { FORM_CONTROL_CLASS } from "../../ui/styles/formStyles";
import AuthCardPage from "./AuthCardPage";
import BackToSignInLink from "./BackToSignInLink";

const RETURN_TO_SIGN_IN_AFTER_MILLISECONDS = 3000;

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [query] = useSearchParams();
  const navigate = useNavigate();
  const token = query.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [mismatch, setMismatch] = useState(false);
  const { state, submit } = useSubmission();

  useEffect(() => {
    if (state.status !== "succeeded") {
      return;
    }
    const timer = window.setTimeout(() => {
      void navigate(appPaths.login());
    }, RETURN_TO_SIGN_IN_AFTER_MILLISECONDS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [state.status, navigate]);

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password !== confirmation) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    submit(() => confirmPasswordReset(token, password));
  };

  if (state.status === "succeeded") {
    return (
      <AuthCardPage title={t("reset.title")}>
        <div className="text-center">
          <div className="app-status-text-success mb-4">
            <CheckIcon className="mx-auto mb-2 h-12 w-12" aria-hidden="true" />
            {t("reset.successTitle")}
          </div>
          <p className="app-text-muted mb-4 text-sm">{t("reset.successMessage")}</p>
          <AppButton type="button" onClick={() => void navigate(appPaths.login())} className="w-full" variant="primary">
            {t("reset.loginNow")}
          </AppButton>
        </div>
      </AuthCardPage>
    );
  }

  return (
    <AuthCardPage title={t("reset.title")}>
      <form onSubmit={handleSubmit}>
        {token === "" ? <StatusMessage tone="error" className="mb-3">{t("reset.noToken")}</StatusMessage> : null}
        <fieldset disabled={state.status === "submitting" || token === ""}>
          <input
            type="password"
            aria-label={t("reset.newPassword")}
            placeholder={t("reset.newPassword")}
            className={`mb-3 ${FORM_CONTROL_CLASS}`}
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
            autoComplete="new-password"
            required
          />
          <input
            type="password"
            aria-label={t("reset.confirmPassword")}
            placeholder={t("reset.confirmPassword")}
            className={`mb-3 ${FORM_CONTROL_CLASS}`}
            value={confirmation}
            onChange={(event) => {
              setConfirmation(event.target.value);
            }}
            autoComplete="new-password"
            required
          />
          <AppButton type="submit" className="w-full" variant="primary">
            {state.status === "submitting" ? t("login.pleaseWait") : t("reset.submit")}
          </AppButton>
        </fieldset>
        {mismatch ? <StatusMessage tone="error" className="mt-3">{t("reset.passwordMismatch")}</StatusMessage> : null}
        {state.status === "failed" ? <ErrorNotice error={state.error} className="mt-3" /> : null}
        <BackToSignInLink />
      </form>
    </AuthCardPage>
  );
}
